import React, { Suspense, useEffect, useState } from "react";
import { useLocation, Routes, Route, useParams } from "react-router-dom";
import { useAppSelector } from "@/core/hooks";
import { useModalRouter } from "@/hooks/useModalRouter";
import { selectModalById } from "@/core/modal";
import { ModalWindow } from "@/components/organisms/ModalWindow";
import { LoadingContainer } from "@/components/molecules";
import { matchModalRoute } from "./ModalMatcher";
import type {
  ModalRoute,
  ModalRouteMatch,
  ModalRouteContext,
} from "@/types/modal";

interface ModalWrapperProps {
  children: React.ReactNode;
}

/**
 * Declarative Modal Loader Component
 *
 * Uses ModalMatcher to dynamically load modal components based on declarative routes configuration.
 * Now uses Redux for modal state management instead of useModalStack.
 */
const DeclarativeModalLoader: React.FC = () => {
  const params = useParams<{ type: string; id?: string }>();
  const location = useLocation();
  const modalRouter = useModalRouter();

  // Component loading state
  const [ModalComponent, setModalComponent] =
    useState<React.ComponentType<any> | null>(null);
  const [componentLoading, setComponentLoading] = useState(true);
  const [componentError, setComponentError] = useState<Error | null>(null);
  const [routeMatch, setRouteMatch] = useState<ModalRouteMatch | null>(null);

  // Get modal ID from location state for state management
  const stateModalId = location.state?.modalId;

  // Get modal state from router
  const isLoading = modalRouter.isLoading(stateModalId);
  const routerError = modalRouter.getError(stateModalId);

  // Get modal from Redux stack for z-index
  const stackModal = useAppSelector((state) =>
    stateModalId ? selectModalById(state, stateModalId) : null,
  );
  const zIndex = stackModal?.zIndex || 1000;

  // Match route and load component
  useEffect(() => {
    if (!params.type) {
      setComponentError(new Error("Modal type not specified"));
      setComponentLoading(false);
      return;
    }

    setComponentLoading(true);
    setComponentError(null);

    // Build path from params
    const path = params.id ? `/${params.type}/${params.id}` : `/${params.type}`;

    // Match route
    const match = matchModalRoute(path);

    if (!match) {
      setComponentError(new Error(`No modal route found for path: ${path}`));
      setComponentLoading(false);
      return;
    }

    setRouteMatch(match);

    // Load component dynamically
    Promise.resolve(match.route.component)
      .then((component) => {
        setModalComponent(() => component);
      })
      .catch((error) => {
        setComponentError(
          error instanceof Error ? error : new Error(String(error)),
        );
      })
      .finally(() => {
        setComponentLoading(false);
      });
  }, [params.type, params.id]);

  // Handle modal close
  const handleClose = () => {
    // Clear modal state before navigation
    if (stateModalId) {
      modalRouter.clearModalState(stateModalId);
    }
    modalRouter.navigateBack();
  };

  // Create modal context for component props
  const modalContext: ModalRouteContext = {
    onClose: handleClose,
    navigateToModal: modalRouter.navigateToModal,
    navigateBack: modalRouter.navigateBack,
    params: routeMatch?.params || {},
    modalType: routeMatch?.modalType || "",
  };

  // Determine overall loading state
  const overallLoading = componentLoading || isLoading;

  // Determine overall error state
  const overallError = componentError || routerError;

  // Don't render if no modal type
  if (!params.type) {
    return null;
  }

  // Get modal options from route or use defaults
  const modalOptions = routeMatch?.route.modalOptions || {};

  return (
    <ModalWindow
      open={true}
      onClose={handleClose}
      loading={overallLoading}
      error={overallError}
      options={{
        zIndex,
        closeOnOverlayClick: true,
        closeOnEscape: true,
        showCloseButton: true,
        modalType: routeMatch?.modalType,
        ...modalOptions, // Merge route-specific options
      }}
    >
      {ModalComponent && routeMatch && !overallLoading && !overallError && (
        <Suspense fallback={<LoadingContainer loading={true} />}>
          <DeclarativeModalContent
            ModalComponent={ModalComponent}
            route={routeMatch.route}
            context={modalContext}
          />
        </Suspense>
      )}
    </ModalWindow>
  );
};

/**
 * Declarative Modal Content Component
 *
 * Renders modal content with props generated by route's getProps function.
 * No more conditional logic - props are defined declaratively in route configuration.
 */
interface DeclarativeModalContentProps {
  ModalComponent: React.ComponentType<any>;
  route: ModalRoute;
  context: ModalRouteContext;
}

const DeclarativeModalContent: React.FC<DeclarativeModalContentProps> = ({
  ModalComponent,
  route,
  context,
}) => {
  // Generate props using route's getProps function
  const componentProps = route.getProps
    ? route.getProps(context.params, context)
    : { onClose: context.onClose };

  return <ModalComponent {...componentProps} />;
};

/**
 * Enhanced Modal Wrapper Component
 *
 * Provides modal overlay functionality with declarative routing system:
 * 1. Detecting background state from location (indicating modal should be open)
 * 2. Rendering children normally when no modal is active
 * 3. Rendering children with background location when modal is active
 * 4. Rendering modal routes overlay using declarative route matching
 * 5. Integrating modal stacking and enhanced state management via Redux
 * 6. Supporting nested background states for proper modal stacking
 */
const ModalWrapperComponent: React.FC<ModalWrapperProps> = ({ children }) => {
  const location = useLocation();

  // Check if we have a background state (indicating we opened a modal)
  const background = location.state && location.state.background;

  // Enhanced background handling for nested modals
  // If we're in a modal route and have background state, use the background
  // This ensures proper rendering of the underlying content
  const shouldUseBackground =
    background && location.pathname.startsWith("/modal/");

  return (
    <>
      {/* Render children normally or with background location when modal is active */}
      <Routes location={shouldUseBackground ? background : location}>
        <Route path="*" element={children} />
      </Routes>

      {/* Render modal overlays when background exists */}
      {background && (
        <Routes>
          <Route path="/modal/:type" element={<DeclarativeModalLoader />} />
          <Route path="/modal/:type/:id" element={<DeclarativeModalLoader />} />
        </Routes>
      )}
    </>
  );
};

export const ModalWrapper: React.FC<ModalWrapperProps> = ModalWrapperComponent;
